import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

const RoleManagement = () => {
  const { isSuperAdmin } = useAuth();
  const canEdit = isSuperAdmin();

  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [editingUserId, setEditingUserId] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editingRolesUserId, setEditingRolesUserId] = useState(null);
  const [rolesDraft, setRolesDraft] = useState([]);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [rolesError, setRolesError] = useState('');

  const loadData = async () => {
    if (!canEdit) {
      setLoading(false);
      setError('Only super admins can manage roles and change member emails.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const [rolesRes, usersRes] = await Promise.all([
        axios.get('/admin/roles'),
        axios.get('/admin/users', { params: { per_page: 200 } }),
      ]);

      setRoles(rolesRes.data.roles || rolesRes.data || []);

      const incomingUsers = usersRes.data.data || usersRes.data.users || usersRes.data;
      setUsers(Array.isArray(incomingUsers) ? incomingUsers : []);
    } catch (err) {
      const message =
        err.response?.data?.message || 'Unable to load roles right now. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  useEffect(() => {
    if (notice) {
      const timer = setTimeout(() => setNotice(''), 3500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [notice]);

  const filteredUsers = useMemo(() => {
    if (selectedRoleId === 'all') return users;
    return users.filter((user) => user.roles?.some((role) => role.id === selectedRoleId));
  }, [selectedRoleId, users]);

  const startEditing = (user) => {
    if (editingRolesUserId) {
      cancelRolesEditing();
    }
    setEditingUserId(user.id);
    setEmailDraft(user.email);
    setEditError('');
  };

  const cancelEditing = () => {
    setEditingUserId(null);
    setEmailDraft('');
    setSaving(false);
    setEditError('');
  };

  const startRolesEditing = (user) => {
    if (editingUserId) {
      cancelEditing();
    }
    setEditingRolesUserId(user.id);
    setRolesDraft(user.roles?.map((role) => role.id) || []);
    setRolesError('');
  };

  const cancelRolesEditing = () => {
    setEditingRolesUserId(null);
    setRolesDraft([]);
    setRolesSaving(false);
    setRolesError('');
  };

  const toggleRoleDraft = (roleId) => {
    setRolesDraft((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    );
  };

  const saveEmail = async () => {
    if (!emailDraft?.trim()) {
      setEditError('Email is required.');
      return;
    }

    setSaving(true);
    setEditError('');

    try {
      const { data } = await axios.put(`/admin/users/${editingUserId}`, {
        email: emailDraft.trim(),
      });

      const updatedUser = data.user || data;

      setUsers((prev) =>
        prev.map((user) => (user.id === updatedUser.id ? { ...user, ...updatedUser } : user)),
      );
      setNotice('Email updated successfully.');
      cancelEditing();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to update email right now.';
      setEditError(message);
    } finally {
      setSaving(false);
    }
  };

  const saveRoles = async () => {
    if (!editingRolesUserId) return;

    setRolesSaving(true);
    setRolesError('');

    try {
      const { data } = await axios.post(`/admin/users/${editingRolesUserId}/roles`, {
        roles: rolesDraft,
      });

      const updatedUser = data.user || data;

      setUsers((prev) =>
        prev.map((user) => (user.id === updatedUser.id ? { ...user, ...updatedUser } : user)),
      );
      setNotice('Roles updated successfully.');
      cancelRolesEditing();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to update roles right now.';
      setRolesError(message);
    } finally {
      setRolesSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        Loading roles and members...
      </div>
    );
  }

  if (error) {
    return <div className="error-banner">{error}</div>;
  }

  return (
    <section style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1.25rem' }}>
      <div className="card" style={{ gap: '0.75rem' }}>
        <div>
          <p className="text-muted" style={{ marginBottom: '0.35rem', fontWeight: 600 }}>
            Roles directory
          </p>
          <h2 style={{ fontSize: '1.6rem' }}>Super admin controls</h2>
          <p className="text-muted">
            Choose a role to see assigned members and update their email or access levels.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setSelectedRoleId('all')}
            className="btn btn-muted"
            style={{
              justifyContent: 'space-between',
              background: selectedRoleId === 'all' ? 'rgba(37, 99, 235, 0.12)' : undefined,
              color: selectedRoleId === 'all' ? '#1d4ed8' : undefined,
            }}
          >
            <span>All members</span>
            <span className="badge badge-muted">{users.length}</span>
          </button>

          {roles.map((role) => (
            <button
              key={role.id}
              type="button"
              onClick={() => setSelectedRoleId(role.id)}
              className="btn btn-muted"
              style={{
                justifyContent: 'space-between',
                background:
                  selectedRoleId === role.id ? 'rgba(37, 99, 235, 0.12)' : 'rgba(15,23,42,0.05)',
                color: selectedRoleId === role.id ? '#1d4ed8' : undefined,
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <strong style={{ display: 'block' }}>{role.name}</strong>
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                  {role.description || 'No description'}
                </span>
              </div>
              <span className="badge badge-muted">{role.users_count ?? 0}</span>
            </button>
          ))}
        </div>

        {!canEdit && (
          <div className="notice notice-info">
            You need super admin permissions to update member emails or roles.
          </div>
        )}
      </div>

      <div className="card" style={{ gap: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '1.4rem' }}>
              {selectedRoleId === 'all'
                ? 'All members'
                : roles.find((role) => role.id === selectedRoleId)?.name || 'Members'}
            </h2>
            <p className="text-muted">
              {canEdit
                ? 'Edit a member to change their email or adjust their roles, then save to apply.'
                : 'View-only access. Super admins can edit emails and roles from here.'}
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={loadData}>
            Refresh
          </button>
        </div>

        {notice && <div className="success-banner">{notice}</div>}
        {editError && editingUserId && <div className="error-banner">{editError}</div>}
        {rolesError && editingRolesUserId && <div className="error-banner">{rolesError}</div>}

        {filteredUsers.length ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Email</th>
                <th>Roles</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const isEditing = editingUserId === user.id;
                const isEditingRoles = editingRolesUserId === user.id;
                const statusLabel = user.status ?? 'inactive';

                return (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.name}</strong>
                      <p style={{ fontSize: '0.85rem' }}>
                        Last login:{' '}
                        {user.last_login_at
                          ? new Date(user.last_login_at).toLocaleString()
                          : 'Never'}
                      </p>
                    </td>
                    <td style={{ minWidth: 260 }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input
                            type="email"
                            value={emailDraft}
                            onChange={(e) => setEmailDraft(e.target.value)}
                            className="input"
                            style={{ minWidth: 220 }}
                            placeholder="name@email.com"
                            disabled={saving}
                          />
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={saveEmail}
                            disabled={saving}
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-muted"
                            onClick={cancelEditing}
                            disabled={saving}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span>{user.email}</span>
                      )}
                    </td>
                    <td>
                      {isEditingRoles ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {roles.map((role) => (
                              <label
                                key={role.id}
                                className="badge badge-muted"
                                style={{ textTransform: 'capitalize', cursor: 'pointer' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={rolesDraft.includes(role.id)}
                                  onChange={() => toggleRoleDraft(role.id)}
                                  disabled={rolesSaving}
                                  style={{ marginRight: '0.35rem' }}
                                />
                                {role.slug}
                              </label>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={saveRoles}
                              disabled={rolesSaving}
                            >
                              {rolesSaving ? 'Saving...' : 'Save roles'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-muted"
                              onClick={cancelRolesEditing}
                              disabled={rolesSaving}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : user.roles?.length ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                          {user.roles.map((role) => (
                            <span
                              key={role.id}
                              className="badge badge-muted"
                              style={{ textTransform: 'capitalize' }}
                            >
                              {role.slug}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No role</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          statusLabel === 'active'
                            ? 'badge-success'
                            : statusLabel === 'suspended'
                              ? 'badge-danger'
                              : 'badge-muted'
                        }`}
                        style={{ textTransform: 'capitalize' }}
                      >
                        {statusLabel}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {canEdit ? (
                        <div className="table-actions">
                          <button
                            type="button"
                            onClick={() => startEditing(user)}
                            disabled={isEditingRoles}
                          >
                            {isEditing ? 'Editing…' : 'Edit email'}
                          </button>
                          <button
                            type="button"
                            onClick={() => startRolesEditing(user)}
                            disabled={isEditing}
                          >
                            {isEditingRoles ? 'Editing…' : 'Edit roles'}
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">View only</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="card empty-state">
            <h3>No members for this role</h3>
            <p>Assign users to this role to manage their access and contact info here.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default RoleManagement;
